/**
 * What a call sign may look like.
 *
 * **Here rather than in the DTO, because both sides need it and a copy would
 * drift.** The server refuses badly-shaped names in `UpdateCallSignDto`; the
 * Account pane greys its Save button out for the same reason, so a player is not
 * invited to submit something that cannot be accepted. Written twice, those two
 * rules diverge the first time either is edited, and the failure is silent in the
 * kind way — the button stays enabled and the server answers with a sentence the
 * player was never given a chance to avoid.
 *
 * `shared/` is the one place both a Nest service and a browser can read, and this
 * is exactly its shape of fact: no Nest, no Prisma, no Node built-in, no clock,
 * no randomness. A regular expression and two integers.
 *
 * ── The rule, and why it is narrower than a name ─────────────────────────────
 *
 * A full name is permissive (`account.dto.ts`) because it is printed on an
 * invoice and has to be able to spell a real person. A call sign is the opposite
 * kind of string: it is an *identifier*, shown to other players in the kill feed,
 * the roster and the debrief, and matched case-insensitively for uniqueness. So
 * it is ASCII, and both ends must be alphanumeric — a leading or trailing dot
 * reads as a truncation, and a name made only of punctuation is not a name.
 *
 * Three is the floor because two characters are not distinguishable in a kill
 * feed. Twenty is the ceiling because the roster column and the plate over a tank
 * are both drawn for it.
 *
 * Uniqueness is deliberately **not** here. It is a fact about the database rather
 * than about the string, and it is enforced where the write happens —
 * `AccountService.updateCallSign`, plus the reserved-name check that covers the
 * AI, which has names but no rows.
 */

/** Shortest call sign the game will accept. */
export const CALL_SIGN_MIN = 3;

/** Longest call sign the game will accept. */
export const CALL_SIGN_MAX = 20;

/**
 * Letters, digits, and `.` `_` `-` inside; alphanumeric at both ends.
 *
 * Anchored, and it must stay anchored: unanchored, this would accept any string
 * that merely *contains* something well formed, which is the classic way a
 * validating regular expression validates nothing.
 */
export const CALL_SIGN_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.-]*[A-Za-z0-9]$/;

/**
 * Whether a call sign is well formed. Length and shape only — never whether it is
 * free, which no caller on the client side can know.
 *
 * Does not trim: callers decide what they are validating. The server validates
 * the trimmed value it is about to store; the pane validates the trimmed value it
 * is about to send.
 */
export function isWellFormedCallSign(callSign: string): boolean {
  return (
    callSign.length >= CALL_SIGN_MIN &&
    callSign.length <= CALL_SIGN_MAX &&
    CALL_SIGN_PATTERN.test(callSign)
  );
}

/**
 * The key a call sign is unique on.
 *
 * Lower case, which is the rule `GarageProvisionService` established when it
 * created the column: `Ironclad` and `ironclad` are one name, as they must be for
 * a name anyone is expected to recognise across a lobby. Exported so the reserved
 * list, the uniqueness read and the write all fold the same way rather than three
 * callers each remembering to.
 */
export function callSignKey(callSign: string): string {
  return callSign.trim().toLowerCase();
}
